/**
 * Google Ads conversion event helpers.
 *
 * Browser events use one delivery path: a single dataLayer push. The GTM
 * container owns the Google Ads and GA4 tags that consume these events.
 */
import { pushToDataLayer } from "@/lib/analytics/gtm-datalayer";
import { createLogger } from "@/lib/logger";

const logger = createLogger("google-ads");

const CONVERSION_IDS = {
  EMAIL_SIGNUP: "AW-16849622442/JcXMCL2-n5saEKqrw-I-",
  OAUTH_SIGNUP: "AW-16849622442/JcXMCL2-n5saEKqrw-I-",
  PURCHASE: "AW-16849622442/EiCuCIyh3agbEKqrw-I-",
  FIRST_CONVERSATION: "AW-16849622442/ovhgCIWg56gbEKqrw-I-",
  ENGAGED_SESSION: "AW-16849622442/ygu3CIXU2qgbEKqrw-I-",
  SEVEN_DAY_STREAK: "AW-16849622442/9fIyCLDM2qgbEKqrw-I-",
} as const;

interface ConversionEventParams {
  value?: number;
  currency?: string;
  transaction_id?: string;
  event_callback?: () => void;
}

function conversionMetadata(sendTo: string, eventCallback?: () => void) {
  const [conversionId, conversionLabel] = sendTo.split("/");

  return {
    google_ads: {
      conversion_id: conversionId.replace("AW-", ""),
      conversion_label: conversionLabel,
    },
    eventCallback,
    eventTimeout: eventCallback ? 1000 : undefined,
  };
}

export function trackEmailSignupConversion(params?: ConversionEventParams) {
  pushToDataLayer({
    event: "sign_up",
    method: "email",
    event_category: "lead",
    event_label: "email_signup",
    value: params?.value ?? 0.1,
    currency: params?.currency ?? "USD",
    ...conversionMetadata(CONVERSION_IDS.EMAIL_SIGNUP, params?.event_callback),
  });

  logger.info("Email signup conversion queued in dataLayer");
}

export function trackOAuthSignupConversion(params?: ConversionEventParams) {
  pushToDataLayer({
    event: "sign_up",
    method: "google",
    event_category: "lead",
    event_label: "oauth_signup",
    value: params?.value ?? 0.1,
    currency: params?.currency ?? "USD",
    ...conversionMetadata(CONVERSION_IDS.OAUTH_SIGNUP, params?.event_callback),
  });

  logger.info("OAuth signup conversion queued in dataLayer");
}

export function trackPurchaseConversion(params: {
  value: number;
  currency?: string;
  transaction_id?: string;
  event_callback?: () => void;
}) {
  const transactionId = params.transaction_id ?? Date.now().toString();
  const currency = params.currency ?? "EUR";

  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: transactionId,
      value: params.value,
      currency,
    },
    event_category: "purchase",
    event_label: "payment_success",
    ...conversionMetadata(CONVERSION_IDS.PURCHASE, params.event_callback),
  });

  logger.info("Purchase conversion queued in dataLayer", { transactionId });
}

export function trackFirstConversation(params?: ConversionEventParams) {
  pushToDataLayer({
    event: "first_conversation_completed",
    event_category: "engagement",
    event_label: "first_conversation",
    value: params?.value ?? 0.3,
    currency: params?.currency ?? "EUR",
    ...conversionMetadata(CONVERSION_IDS.FIRST_CONVERSATION, params?.event_callback),
  });

  logger.info("First conversation conversion queued in dataLayer");
}

export function trackEngagedSession(
  params?: ConversionEventParams & { duration_minutes?: number }
) {
  pushToDataLayer({
    event: "engaged_session",
    event_category: "engagement",
    event_label: "engaged_session_5min",
    duration_minutes: params?.duration_minutes ?? 5,
    value: params?.value ?? 1,
    currency: params?.currency ?? "EUR",
    ...conversionMetadata(CONVERSION_IDS.ENGAGED_SESSION, params?.event_callback),
  });

  logger.info("Engaged session conversion queued in dataLayer", {
    durationMinutes: params?.duration_minutes ?? 5,
  });
}

export function trackSevenDayStreak(params?: ConversionEventParams & { streak_days?: number }) {
  pushToDataLayer({
    event: "achievement_unlocked",
    event_category: "engagement",
    event_label: "seven_day_streak",
    achievement_type: "streak",
    streak_days: params?.streak_days ?? 7,
    value: params?.value ?? 1,
    currency: params?.currency ?? "EUR",
    ...conversionMetadata(CONVERSION_IDS.SEVEN_DAY_STREAK, params?.event_callback),
  });

  logger.info("7-day streak conversion queued in dataLayer");
}

export function trackConversionWithCallback(
  trackingFn: () => void,
  callback: () => void,
  timeout = 1000
) {
  let callbackCalled = false;

  const safeCallback = () => {
    if (!callbackCalled) {
      callbackCalled = true;
      callback();
    }
  };

  setTimeout(safeCallback, timeout);
  trackingFn();
}
